import React, { useCallback, useEffect, useMemo, useState } from "react";
import "../css/inbox.css";
import MainBreadcrumb from "../../../../component-controller-mod/component-serv/navigation-fs-cp/breadcrumb-nav-op/page-main-bcb/ts/main-bcb";
import SelectField from "../../../../component-controller-mod/component-serv/input-fs-cp/select-field-op/ts/selectField";
import SearchField from "../../../../component-controller-mod/component-serv/input-fs-cp/search-field-op/ts/searchField";
import { batchDeleteInboxContacts, columns, deleteInboxItem, formatBytes, getInboxApi, getInboxDetailApi, normalizeSearch, status, toBadgeVariant, TypeBussiness } from "./inbox.type";
import { Table, Pagination, Button, Confirmation, Empty } from "@core/uilibrary";
import type { InboxItem, InboxRow } from "./inbox.type";
import { DeleteConfirmationIcon, EyeIcon, TrashIcon } from "../../../../asset-controller-mod/asset-serv/icons/iconExporter";
import StatusBadge from "../../../../component-controller-mod/component-serv/badge-fs-cp/dynamic-badge-op/ts/statusBadge";
import { useGlobalPopupProvider } from "../../../../context-controller-mod/context-serv/popup-provider/ts/globalPopupProvider";
import { useToast } from "../../../../context-controller-mod/context-serv/toast-provider/bottomToast/ts/ToastProvider";
import { useRightSidebar } from "../../../../context-controller-mod/context-serv/global-provider/right-sidebar-provider/ts/right-sidebar-context";
import UrsbInbox from "./ursb-inbox";
import { groupPermissionAccessInbox } from "../../../../context-controller-mod/context-serv/permission-provider/permissionExporter";
import CancelButton from '../../../../component-controller-mod/component-serv/button-fs-cp/cancel-button-op/ts/cancelButton';

const InboxUi = () => {
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState<InboxRow[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalItems, setTotalItems] = useState(0);
    const [statusValue, setStatusValue] = useState<string>("");
    const [typeValue, setTypeValue] = useState<string>("");
    const { openConfirm, closeConfirm } = useGlobalPopupProvider();
    const [saving, setSaving] = useState(false);
    const { showToast } = useToast();
    const ursb = useRightSidebar();
    const [detailLoading, setDetailLoading] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);
    const [searchValue, setSearchValue] = useState("");
    const [searchDebounced, setSearchDebounced] = useState("");
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const clearSelection = () => setSelectedIds(new Set());

    const canDelete = groupPermissionAccessInbox.delete()

    const markRowRead = useCallback((inboxId: string) => {
        setRows((prev) =>
            prev.map((r) =>
                r.id === inboxId
                    ? {
                        ...r,
                        Status: "read",
                        _raw: r._raw ? { ...r._raw, status: "read" } : r._raw,
                    }
                    : r
            )
        );
    }, []);

    const openProfile = useCallback(async (item: InboxItem) => {
        const inboxUuid = String(item.id ?? "");
        if (!inboxUuid) return;
        markRowRead(inboxUuid);

        ursb.open({
            title: "View Inbox",
            widthSize: "400px",
            content: (
                <UrsbInbox
                    fullname="-"
                    type="-"
                    email="-"
                    phone="-"
                    message="-"
                    status="all-status"
                    onBack={() => ursb.close()}
                />
            ),
        });

        try {
            setDetailLoading(true);

            const res = await getInboxDetailApi(inboxUuid);
            const detailData = (res as any)?.data?.data;

            if (detailData?.status === "read") {
                markRowRead(inboxUuid);
            }

            const first = detailData?.attachments?.[0];

            ursb.open({
                title: "View Inbox",
                widthSize: "400px",
                content: (
                    <UrsbInbox
                        fullname={detailData?.fullName ?? "-"}
                        type={detailData?.categoryName ?? "-"}
                        email={detailData?.email ?? "-"}
                        phone={detailData?.phone ?? "-"}
                        message={detailData?.message ?? "-"}
                        status={detailData?.status ?? "all-status"}
                        file={
                            first
                                ? {
                                    name: first.filename,
                                    sizeLabel: formatBytes(first.fileSize),
                                    url: first.url ?? undefined,
                                }
                                : undefined
                        }
                        onBack={() => ursb.close()}
                    />
                ),
            });
        } catch (e) {
            console.error("GET inbox detail failed:", e);

            ursb.open({
                title: "View Inbox",
                widthSize: "400px",
                content: (
                    <UrsbInbox
                        fullname="-"
                        type="-"
                        email="-"
                        phone="-"
                        message="Failed to load detail."
                        status="all-status"
                        onBack={() => ursb.close()}
                    />
                ),
            });
        } finally {
            setDetailLoading(false);
        }
    }, [ursb, markRowRead]);

    useEffect(() => {
        let alive = true;

        const run = async () => {
            try {
                setLoading(true);

                const res = await getInboxApi(currentPage, rowsPerPage, {
                    status: statusValue,
                    category: typeValue,
                    search: searchDebounced,
                });
                if (!alive) return;

                const payload = res?.data?.data;
                const items = payload?.items ?? [];
                const pagination = payload?.pagination;

                setTotalItems(pagination?.totalRecords ?? 0);

                const mapped: InboxRow[] = items.map((item: InboxItem, idx: number) => {
                    const sequentialId = (currentPage - 1) * rowsPerPage + idx + 1;

                    return {
                        id: String(item.id ?? sequentialId),
                        ID: String(sequentialId),
                        "Contact name": item.fullName ?? item.name ?? "-",
                        Type: (item as any).categoryName ?? "-",
                        Email: item.email ?? "-",
                        Phone: item.phone ?? item.phoneNumber ?? "-",
                        Status: item.status ?? "-",
                        _raw: item,
                    };
                });

                setRows(mapped);
            } catch (e) {
                console.error("GET inbox failed:", e);
            } finally {
                if (alive) setLoading(false);
            }
        };

        run();
        return () => {
            alive = false;
        };
    }, [currentPage, rowsPerPage, statusValue, typeValue, searchDebounced, reloadKey]);

    useEffect(() => {
        const t = globalThis.window.setTimeout(() => setSearchDebounced(normalizeSearch(searchValue)), 400);
        return () => globalThis.window.clearTimeout(t);
    }, [searchValue]);

    const handleConfirmRequest = useCallback((clickedId: string) => {
        const idsToDelete =
            selectedIds.size > 0
                ? Array.from(selectedIds)
                : [clickedId];

        openConfirm(
            <Confirmation
                visible
                title={idsToDelete.length > 1 ? "Delete contacts?" : "Delete contact?"}
                description={
                    idsToDelete.length > 1
                        ? `Are you sure you want to delete ${idsToDelete.length} contacts? This action cannot be undone.`
                        : "Are you sure you want to delete this contact? This action cannot be undone?"
                }
                status="error"
                onClose={closeConfirm}
                icon={<DeleteConfirmationIcon/>}
                buttons={
                    <>
                        <Button
                            variant="ghost"
                            size="md"
                            value="Cancel"
                            type="button"
                            disabled={saving}
                            onClick={() => closeConfirm()}
                        />

                        <Button
                            variant="danger"
                            size="md"
                            value={saving ? "Saving..." : "Confirm"}
                            type="button"
                            disabled={saving}
                            onClick={async () => {
                                try {
                                    setSaving(true);

                                    if (idsToDelete.length > 1) {
                                        const res = await batchDeleteInboxContacts(idsToDelete);
                                        if ((res as any)?.status === 204 || (res as any)?.data?.success) {
                                            showToast({
                                                status: "success",
                                                title: "Delete success",
                                                description: `${idsToDelete.length} contacts deleted successfully.`,
                                            });
                                        } else {
                                            showToast({
                                                status: "warning",
                                                title: "Uncompleted!",
                                                description: "No contacts were deleted.",
                                            });
                                        }
                                    } else {
                                        const res = await deleteInboxItem(idsToDelete[0]);
                                        if (res?.status === 204) {
                                            showToast({
                                                status: "success",
                                                title: "Delete success",
                                                description: "Contact deleted successfully.",
                                            });
                                        } else {
                                            showToast({
                                                status: "warning",
                                                title: "Uncompleted!",
                                                description: "No contacts were deleted.",
                                            });
                                        }
                                    }

                                    closeConfirm();

                                    clearSelection();

                                    setReloadKey((k) => k + 1);
                                } catch (e) { //NOSONAR
                                    showToast({
                                        status: "error",
                                        title: "Uncompleted!",
                                        description: "Contact(s) cannot be deleted.",
                                    });
                                } finally {
                                    setSaving(false);
                                }
                            }}
                        />
                    </>
                }
            />
        );
    }, [openConfirm, closeConfirm, saving, selectedIds, showToast]);

    const formattedData = useMemo(
        () =>
            rows.map((row) => ({
                ...row,
                Status: <StatusBadge
                    variant={toBadgeVariant(row.Status)}
                    label={row.Status ? row.Status.charAt(0).toUpperCase() + row.Status.slice(1) : '-'}
                    />,
                Action: (
                    <div className="scl--ibx-action-const-sty">
                        <button
                            className="scl--ibx-eye-btn-const-sty"
                            type="button"
                            onClick={() => openProfile(row._raw!)}
                        >
                            <EyeIcon />
                        </button>
                        {canDelete &&
                            <>
                                <span>|</span>
                                <button
                                    className="scl--ibx-tras-btn-const-sty"
                                    type="button"
                                    onClick={() => handleConfirmRequest(row.id)}
                                >
                                    <TrashIcon />
                                </button>
                            </>
                        }
                    </div>
                ),
            })),
        [rows, openProfile, handleConfirmRequest]
    );
    const hasActiveFilters = Boolean(
        (statusValue && statusValue !== 'all') ||
        (typeValue   && typeValue   !== 'all') ||
        searchValue
        );

        const handleClearFilters = useCallback(() => {
        setStatusValue('');
        setTypeValue('');
        setSearchValue('');
        setCurrentPage(1);
    }, []);

    return (
        <main className="scl--inbox-main-content">
            <div className="scl--ibx-head-const-box">
                <MainBreadcrumb
                    title="Inbox"
                    paragraph="View displays messages from other users as well as automated messages associated with scheduled actions."
                />
            </div>

            <div className="scl--ibx-filt-const-box">
                <div className="scl--ibx-filt-status">
                    <SelectField
                        name="status"
                        placeholder="Status"
                        label="Status"
                        options={status}
                        value={statusValue}
                        onChange={(v) => {
                            setStatusValue(String(v));
                            setCurrentPage(1);
                        }}
                    />
                </div>

                <div className="scl--ibx-filt-type">

                    <SelectField
                        name="type"
                        placeholder="Type"
                        label="Type"
                        options={TypeBussiness}
                        value={typeValue}
                        onChange={(v) => {
                            setTypeValue(String(v));
                            setCurrentPage(1);
                        }}
                    />
                </div>

                <div className="scl--ibx-filt-search">
                    <SearchField
                        name="search"
                        placeholder="Search"
                        label="Search"
                        value={searchValue}
                        onChange={(v) => {
                            setSearchValue(v);
                            setCurrentPage(1);
                        }}
                    />
                </div>
                {hasActiveFilters && (
                        <CancelButton onClick={handleClearFilters} aria-label="Clear all filters" />
                    )}
            </div>

            <div className="scl--ibx-tabl-const-box">
                {rows.length === 0 && !loading ? (
                    <Empty
                        title="No Data yet"
                        description="There's nothing here yet. Start by adding new data to see it listed here."
                    />
                ) : (
                    <>
                        <Table
                            key={rows.map(r => `${r.id}:${r.Status}`).join("|")}
                            columns={columns}
                            data={formattedData}
                            showCheckbox
                            showId={false}
                            showPagination={false}
                            rowsPerPageOptions={[5, 10, 20]}
                            defaultRowsPerPage={10}
                            backgroundColor="transparent"
                            clickableColumns={["Contact name"]}
                            onCellClick={(row: any) => { openProfile(row._raw!) }}
                            onSelectionChange={(selectedRowIds: string[]) => {
                                setSelectedIds(new Set(selectedRowIds));
                            }}
                            selectColumnWidth={49}
                            columnWidths={{
                                ID: "4%",
                                "Contact name": "20%",
                                Type: "10%",
                                Email: "25%",
                                Phone: "15%",
                                Status: "10%",
                                Action: "13%",
                            }}
                        />

                        <Pagination
                            rowsPerPage={rowsPerPage}
                            setRowsPerPage={setRowsPerPage}
                            rowsPerPageOptions={[5, 10, 20]}
                            currentPage={currentPage}
                            setCurrentPage={setCurrentPage}
                            totalItems={totalItems}
                        />
                    </>
                )}

            </div>
        </main>
    );
};

export default InboxUi;